/**
 * MobileCollabsTopMenu
 *
 * Mobile-optimized top navigation for CollabsView.
 * Replaces the desktop CollabsTopMenu when isMobile === 2.
 * Uses MUI Tabs for view switching and MUI IconButton for actions.
 */
import React from "react";
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Langs from "../../../app/lang/langs";
import { checkPermission } from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

const iconButtonSx = {
  minWidth: 44,
  minHeight: 44,
  borderRadius: "12px",
  color: "rgba(0,0,0,0.7)",
  transition: TRANSITION_SMOOTH,
  "&:hover": {
    backgroundColor: "rgba(255,144,21,0.08)",
    color: "rgb(255,144,21)",
    boxShadow: "0 2px 8px rgba(255,144,21,0.15)",
    transform: "scale(1.06)",
  },
  "&:active": {
    transform: "scale(0.96)",
  },
};

const primaryIconButtonSx = {
  ...iconButtonSx,
  "&:hover": {
    backgroundColor: "rgba(255,144,21,0.08)",
    color: "rgb(255,144,21)",
    transform: "scale(1.06)",
    boxShadow: "0 2px 8px rgba(255,144,21,0.15)",
  },
  "&:active": {
    transform: "scale(0.96)",
  },
};

/**
 * @param {Object} props
 * @param {number} props.isMobile
 * @param {Array}  props.viewsOptions - [{label, value}, ...]
 * @param {number} props.selectedView
 * @param {Function} props.onViewChange
 * @param {Function} props.onExport
 * @param {Function} props.onAddExterne
 * @param {Function} props.onAddWarning
 * @param {Function} props.onAddSpecialCase
 */
export default function MobileCollabsTopMenu({
  isMobile,
  viewsOptions,
  selectedView,
  onViewChange,
  onExport,
  onAddExterne,
  onAddWarning,
  onAddSpecialCase,
  onAddEmploymentPromise,
}) {
  const hasWarningsView = checkPermission(config.scopes.warnings, config.permissionTypes.view);
  const hasCollabsView = checkPermission(config.scopes.collabs, config.permissionTypes.view);
  const hasWarningsEdit = checkPermission(config.scopes.warnings, config.permissionTypes.edit);
  const hasCollabsEdit = checkPermission(config.scopes.collabs, config.permissionTypes.edit);
  const hasClientTeamsEdit = checkPermission(config.scopes.clientsTeams, config.permissionTypes.edit);

  const handleTabChange = (_event, newValue) => {
    onViewChange(newValue);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        bgcolor: "rgba(255,255,255,0.78)",
        backdropFilter: "blur(20px) saturate(1.4)",
        WebkitBackdropFilter: "blur(20px) saturate(1.4)",
        borderBottom: "1px solid rgba(0,40,128,0.06)",
        boxShadow:
          "0 4px 6px -4px rgba(0,40,128,0.05), 0 2px 3px -2px rgba(0,0,0,0.03)",
        px: 1.5,
        pt: 0.75,
        pb: 0,
        transition: TRANSITION_SMOOTH,
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, rgba(255,144,21,0.15), transparent)",
        },
      }}
    >
      {/* Action buttons row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 48,
          px: 0.5,
        }}
      >
        <Box
          sx={{
            fontWeight: 700,
            fontSize: "1.15rem",
            color: "rgb(0,0,0)",
            pl: 0.5,
            letterSpacing: "-0.025em",
            transition: "color 0.2s ease",
          }}
        >
          {viewsOptions.find((o) => o.value == selectedView)?.label}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* Export button (not shown for employment promises view) */}
          {selectedView != 3 && (
            <Tooltip title="Export">
              <IconButton
                onClick={onExport}
                size="large"
                sx={iconButtonSx}
              >
                <FileDownloadOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Add externe button (collabs view) */}
          {hasCollabsEdit && hasClientTeamsEdit && selectedView == 0 && (
            <Tooltip title={<Langs str="ADD_EXTERNE" />}>
              <IconButton
                onClick={onAddExterne}
                size="large"
                sx={iconButtonSx}
              >
                <PersonAddAltOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Add warning button (warnings view) */}
          {hasWarningsEdit && selectedView == 1 && (
            <Tooltip title={<Langs str="ADD_WARNING" />}>
              <IconButton
                onClick={onAddWarning}
                size="large"
                color="warning"
                sx={primaryIconButtonSx}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Add special case button */}
          {hasCollabsEdit && selectedView == 2 && (
            <Tooltip title={<Langs str="ADD" />}>
              <IconButton
                onClick={onAddSpecialCase}
                size="large"
                color="warning"
                sx={primaryIconButtonSx}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Add employment promise button */}
          {hasCollabsEdit && selectedView == 3 && (
            <Tooltip title={<Langs str="ADD_EMPLOYMENT_PROMISE" />}>
              <IconButton
                onClick={onAddEmploymentPromise}
                size="large"
                color="warning"
                sx={primaryIconButtonSx}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Tabs row for view switching */}
      {hasCollabsView && (
        <Tabs
          value={selectedView}
          onChange={handleTabChange}
          variant="fullWidth"
          centered
          sx={{
            minHeight: 40,
            "& .MuiTab-root": {
              minHeight: 40,
              minWidth: 0,
              px: 1.5,
              py: 0.5,
              fontSize: "0.8rem",
              fontWeight: 600,
              textTransform: "none",
              color: "rgba(255,144,21,0.55)",
              borderRadius: "10px 10px 0 0",
              transition: TRANSITION_SMOOTH,
              "&:hover": {
                color: "rgba(255,144,21,0.8)",
                backgroundColor: "rgba(255,144,21,0.06)",
              },
              "&.Mui-selected": {
                fontWeight: 700,
                color: "rgb(255,144,21)",
              },
            },
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
              background: "linear-gradient(90deg, #FF8701, #FFC100)",
              boxShadow: "0 0 8px rgba(255,144,21,0.3)",
              transition: "all 350ms cubic-bezier(0.4, 0, 0.2, 1)",
            },
          }}
        >
          {viewsOptions.map((option) => (
            <Tab
              key={option.value}
              label={option.label}
              value={option.value}
            />
          ))}
        </Tabs>
      )}
    </Box>
  );
}
